import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import TextField from 'components/TextField';

import { useErrorHandler } from 'lib';
import { useDialog } from 'components/Dialog';

import { LabelListAddMutation } from '__generated__/LabelListAddMutation.graphql';

type FormFields = {
  name: string;
};

type Props = {
  connectionID: string;
};

const initial = {
  name: '',
};

const LabelListAdd: FC<Props> = ({ connectionID }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const [add, adding] = useMutation<LabelListAddMutation>(graphql`
    mutation LabelListAddMutation(
      $input: CreateLabelInput!
      $connections: [ID!]!
    ) {
      createLabel(input: $input) {
        node @appendNode(connections: $connections, edgeTypeName: "LabelEdge") {
          ...LabelListRow_label
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { connections: [connectionID], input },
        ...errorHandler({
          onCompleted: () => {
            close();
          },
        }),
      });
    },
    [add, close, connectionID, errorHandler]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Nauja etiketė</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują etiketę. Etiketės atvaizduojamos kelionės kortelėje
            virš nuotraukos.
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default LabelListAdd;
